import React from "react";
import {Formik, FormikHelpers} from "formik";
import * as Yup from "yup";
import {Button, Grid, TextField, Link} from "@material-ui/core";

import "../styles/login.page.css";
import MainPage from "./main.page";
import MerchantOnboardingStatus from "../services/merchant.onboarding.status.service";
import LottieAnimation from "../components/lottie.animation.component";
import searchingContrast from "../animations/searching-contrast.json";
import loader from "../animations/loader.json";
import {PHASES} from "../utils/constants";
import GoogleAnalyticsUtil from "../utils/google.analytics.util";

import DescriptionBlock from "../components/DescriptionBlock/DescriptionBlock";
import Logo from "../components/Logo/logo";
import {ReactComponent as IconArrow} from "../media/icons/bi_arrow-left.svg";

import {
    PageFirstRow,
    Body,
    Footer,
    InputField,
    FormButton,
    Subtitle,
    FormPanel,
    FirstPanel,
    Label,
    DescriptionContainer,
    HeroMediaContainer,
    FormValidationMessage,
    FormHeading,
    FormSubtitle,
    FooterBody,
    FormLogin,
    FirstPanelText,
    PageLastRow,
    HeroMedia,
} from "./login/login.styled";

export interface LoginFormValuesInterface {
    email: string;
    pipefyId: string;
}

interface LoginPageStateInterface {
    showLoginPage: boolean;
    showMainPage: boolean;
    showErrorIcon: boolean;
    showLoadingIcon: boolean;
    showStandardText: boolean;
    errorMessage: string;
    activePhase: string;
    merchantName: string;
    descriptor: string;
    highestCompletedOrder: number;
    phases: Array<any>;
}

class LoginPage extends React.Component<any, LoginPageStateInterface> {
    private _googleAnalytics: GoogleAnalyticsUtil = new GoogleAnalyticsUtil();
    private readonly _merchantOnboardingStatusService: MerchantOnboardingStatus = new MerchantOnboardingStatus();
    private readonly _defaultErrorMessage: string =
        "An error occurred on the server side";

    constructor(props: any) {
        super(props);

        this.state = {
            showLoginPage: true,
            showMainPage: false,
            showErrorIcon: false,
            showLoadingIcon: false,
            showStandardText: true,
            errorMessage: this._defaultErrorMessage,
            activePhase: PHASES.ACCOUNT_VERIFICATION.name,
            merchantName: "Test",
            descriptor: "Test descriptor",
            highestCompletedOrder: 0,
            phases: [],
        };
    }

    componentDidMount(): void {
        this._googleAnalytics.initialize();
        this._googleAnalytics.trackPage("Login Page");
    }

    sendUserCredentials = (parameters: LoginFormValuesInterface): void => {
        this._googleAnalytics.trackEvent("continue");

        this.setState({
            showLoadingIcon: true,
            showStandardText: false,
            showErrorIcon: false,
        });

        const url: string = process.env.REACT_APP_BACK_END_HOST || "";

        const postData = {
            email: parameters.email,
            pipefyId: parameters.pipefyId,
        };

        const successCallback = (response: any) => {
            const phases = response["phases"] || [];
            const activePhase = response["phaseName"] || PHASES.ACCOUNT_VERIFICATION.name;
            const merchantName = response["merchantName"] || this.state.merchantName;
            const descriptor = response["descriptor"] || this.state.descriptor;
            const highestCompletedOrder = response["activePhaseOrder"] || 0;

            this._googleAnalytics.setDimensions(
                merchantName,
                parameters.pipefyId,
                activePhase
            );

            this.setState({
                phases,
                highestCompletedOrder,
                activePhase,
                merchantName,
                descriptor,
                showMainPage: true,
                showLoginPage: false,
                showLoadingIcon: false,
                showStandardText: true,
            });
        };

        const errorCallback = (response: any) => {
            const errorMessage = response.message || this._defaultErrorMessage;
            this.setState({
                showErrorIcon: true,
                showLoadingIcon: false,
                errorMessage,
            });
        };

        this._merchantOnboardingStatusService.postUserInformation(
            url,
            postData,
            successCallback,
            errorCallback
        );
    };

    resetFormIconValues = () => {
        this.setState({
            showErrorIcon: false,
            showLoadingIcon: false,
            showStandardText: true,
        });
    };

    render():
        | React.ReactElement<any, string | React.JSXElementConstructor<any>>
        | string
        | number
        | {}
        | React.ReactNodeArray
        | React.ReactPortal
        | boolean
        | null
        | undefined {
        const errorMessage = (
            <FormValidationMessage>{this.state.errorMessage}</FormValidationMessage>
        );

        const loginPage = (
            <Grid container spacing={0} justify="center" direction="column">
                <PageFirstRow item>
                    <Logo/>
                    <Subtitle variant="subtitle1" align="center" color="textSecondary">
                        simple. seamless. secure
                    </Subtitle>
                </PageFirstRow>

                <PageLastRow>
                    <Body item container spacing={0}>
                        <FirstPanel
                            item
                            container
                            justify="center"
                            alignItems="center"
                            direction="column"
                            md={6}
                        >
                            <HeroMediaContainer>
                                <HeroMedia>
                                    <LottieAnimation
                                        width={317}
                                        height={230}
                                        isActive
                                        lottiAnimation={searchingContrast}
                                    />
                                </HeroMedia>
                            </HeroMediaContainer>

                            <FirstPanelText item container direction="column">
                                <DescriptionBlock/>
                            </FirstPanelText>
                        </FirstPanel>

                        <FormPanel
                            item
                            container
                            alignItems="center"
                            direction="column"
                            md={6}
                            xs={12}
                        >
                            <FormHeading as="h2">Activation Progress</FormHeading>

                            <FormSubtitle as="h3">
                                Track your progress to start accepting payments online.
                            </FormSubtitle>

                            <Formik
                                initialValues={{
                                    email: "",
                                    pipefyId: "",
                                }}
                                onSubmit={(
                                    values: LoginFormValuesInterface,
                                    {setSubmitting}: FormikHelpers<LoginFormValuesInterface>
                                ) => {
                                    // Has to be present does not get used
                                }}
                                validationSchema={Yup.object({
                                    email: Yup.string()
                                        .required("Required")
                                        .email("Invalid email address"),
                                    pipefyId: Yup.number().required("Required"),
                                })}
                                validateOnMount={true} // For some reason the isValid is true on mount, this changes it to false
                                initialErrors={{
                                    // Without this the values are perceived as valid
                                    email: "",
                                    pipefyId: "",
                                }}
                            >
                                {(formik) => (
                                    <FormLogin
                                        onSubmit={(event) => {
                                            event.preventDefault();
                                        }}
                                    >
                                        <InputField item>
                                            <Label>Email Address</Label>

                                            <TextField
                                                value={formik.values.email}
                                                onChange={formik.handleChange}
                                                onBlur={(event) => {
                                                    this.resetFormIconValues();
                                                    formik.handleBlur(event);
                                                }}
                                                error={formik.touched.email && !!formik.errors.email}
                                                helperText={
                                                    (formik.touched.email && formik.errors.email) || ""
                                                }
                                                type="email"
                                                placeholder="email@address.co.za"
                                                fullWidth
                                                name="email"
                                                variant="outlined"
                                                required
                                                autoFocus
                                            />
                                        </InputField>

                                        <InputField item container>
                                            <Label>Application ID</Label>

                                            <TextField
                                                value={formik.values.pipefyId}
                                                onChange={event => {
                                                    event.preventDefault();
                                                    const {value} = event.target || "";
                                                    const regexNumericOnly = /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
                                                    if ((value === "" || regexNumericOnly.test(value)) && value.indexOf(".") === -1 && value.length < 11) {
                                                        formik.setFieldValue("pipefyId", value);
                                                    }
                                                }}
                                                onBlur={(event) => {
                                                    this.resetFormIconValues();
                                                    formik.handleBlur(event);
                                                }}
                                                error={
                                                    formik.touched.pipefyId && !!formik.errors.pipefyId
                                                }
                                                helperText={
                                                    (formik.touched.pipefyId && formik.errors.pipefyId) ||
                                                    ""
                                                }
                                                type="tel"
                                                placeholder="123456789"
                                                fullWidth
                                                name="pipefyId"
                                                variant="outlined"
                                                required
                                            />
                                        </InputField>

                                        <FormButton item container justify="center">
                                            <Button
                                                variant="contained"
                                                type="submit"
                                                size="large"
                                                color="secondary"
                                                aria-label="View your status"
                                                fullWidth
                                                disableElevation
                                                disabled={!formik.isValid}
                                                endIcon={
                                                    this.state.showLoadingIcon ? (
                                                        <LottieAnimation
                                                            isActive={true}
                                                            lottiAnimation={loader}
                                                            height={30}
                                                            width={30}
                                                        />
                                                    ) : (
                                                        <IconArrow/>
                                                    )
                                                }
                                                onClick={() => {
                                                    this.sendUserCredentials({
                                                        email: formik.values.email,
                                                        pipefyId: formik.values.pipefyId,
                                                    });
                                                }}
                                            >
                                                Continue
                                            </Button>
                                        </FormButton>

                                        <Grid item>{this.state.showErrorIcon && errorMessage}</Grid>
                                    </FormLogin>
                                )}
                            </Formik>
                        </FormPanel>

                        <DescriptionContainer>
                            <DescriptionBlock/>
                        </DescriptionContainer>
                    </Body>

                    <Footer justify="center" container>
                        <FooterBody>
                            New to Peach Payments?{" "}
                            <Link onClick={() => {
                                this._googleAnalytics.trackEvent("contact_us");
                            }}
                                  underline="always"
                                  href="https://www.peachpayments.com/#/resources/contact"
                            >
                                Contact Us
                            </Link>
                        </FooterBody>
                    </Footer>
                </PageLastRow>
            </Grid>
        );

        return (
            <>
                {this.state.showLoginPage && loginPage}
                {this.state.showMainPage && (
                    <MainPage
                        googleAnalytics={this._googleAnalytics}
                        activePhase={this.state.activePhase}
                        merchantName={this.state.merchantName}
                        phases={this.state.phases}
                        highestCompletedOrder={this.state.highestCompletedOrder}
                        descriptor={
                            "Complete all of the steps below to finalise your activation with Peach Payments."
                        }
                    />
                )}
            </>
        );
    }
}

export default LoginPage;
